import { Empty } from '@bufbuild/protobuf';

import {
  AdminMFA_EnforceReq,
  AdminMFA_StatusResp,
  AdminMFA_UserReq,
  AuthState,
  MFA_Enroll_BeginReq,
  MFA_Enroll_BeginResp,
  MFA_Enroll_ConfirmReq,
  MFA_Enroll_ConfirmResp,
  MFA_SignIn_VerifyReq,
} from 'protocol/api/auth/dto_auth_pb';
import {
  LogoutReq,
  OrganizationAPIKey_CreateReq,
  OrganizationAPIKey_CreateResp,
  OrganizationAPIKey_DeleteReq,
  OrganizationAPIKey_DeleteResp,
  OrganizationAPIKey_ListReq,
  OrganizationAPIKey_ListResp,
  RefreshResp,
  SignInReq,
  UserPermissions_GetReq,
  UserPermissions_GetResp,
  UserPermissions_SetReq,
  UserPermissions_SetResp,
} from 'protocol/api/auth/svc_authentication_pb';

import api, { Authentication } from '@/shared/api/api';
import { SliceName } from '@/shared/config';
import { createThunkGenerator } from '@/shared/lib';


type AuthRequests = keyof typeof Authentication['methods'];

const generateAuthThunk = createThunkGenerator<AuthRequests>(SliceName.auth);

export const refresh = generateAuthThunk<Empty, RefreshResp>(
  'refresh',
  api.auth.refresh,
);

export const signIn = generateAuthThunk<SignInReq, AuthState>(
  'signIn',
  api.auth.signIn,
);

export const logout = generateAuthThunk<LogoutReq, Empty>(
  'logout',
  api.auth.logout,
);

export const createOrganizationApiKey = generateAuthThunk<
  OrganizationAPIKey_CreateReq,
  OrganizationAPIKey_CreateResp
>('organizationAPIKey_Create', api.auth.organizationAPIKey_Create);

export const listOrganizationApiKeys = generateAuthThunk<
  OrganizationAPIKey_ListReq,
  OrganizationAPIKey_ListResp
>('organizationAPIKey_List', api.auth.organizationAPIKey_List);

export const deleteOrganizationApiKey = generateAuthThunk<
  OrganizationAPIKey_DeleteReq,
  OrganizationAPIKey_DeleteResp
>('organizationAPIKey_Delete', api.auth.organizationAPIKey_Delete);

export const getUserPermissions = generateAuthThunk<
  UserPermissions_GetReq,
  UserPermissions_GetResp
>('userPermissions_Get', api.auth.userPermissions_Get);

// Same RPC as getUserPermissions, but a separate action type: authSlice
// extraReducers must track only the signed-in user's own permissions, while
// getUserPermissions is also used to read other users' permissions
const generateMyPermissionsThunk =
  createThunkGenerator<'myPermissions_Get'>(SliceName.auth);

export const getMyPermissions = generateMyPermissionsThunk<
  UserPermissions_GetReq,
  UserPermissions_GetResp
>('myPermissions_Get', api.auth.userPermissions_Get);

export const setUserPermissions = generateAuthThunk<
  UserPermissions_SetReq,
  UserPermissions_SetResp
>('userPermissions_Set', api.auth.userPermissions_Set);

// Admin Security tab: Status needs MFA_CanView, Enforce needs MFA_CanEnforce
// and Remove needs MFA_CanSetup; any action on an admin target additionally
// requires MFA_CanEditAdmin (the backend refuses the call otherwise)
export const getAdminMFAStatus = generateAuthThunk<
  AdminMFA_UserReq,
  AdminMFA_StatusResp
>('adminMFA_Status', api.auth.adminMFA_Status);

export const setAdminMFAEnforcement = generateAuthThunk<
  AdminMFA_EnforceReq,
  AdminMFA_StatusResp
>('adminMFA_Enforce', api.auth.adminMFA_Enforce);

export const removeAdminMFA = generateAuthThunk<
  AdminMFA_UserReq,
  AdminMFA_StatusResp
>('adminMFA_Remove', api.auth.adminMFA_Remove);

// Self-setup runs on the signed-in admin's own session, so the public enroll
// RPCs are reused instead of the admin MFA ones
export const beginMFAEnroll = generateAuthThunk<
  MFA_Enroll_BeginReq,
  MFA_Enroll_BeginResp
>('mFA_Enroll_Begin', api.auth.mFA_Enroll_Begin);

export const confirmMFAEnroll = generateAuthThunk<
  MFA_Enroll_ConfirmReq,
  MFA_Enroll_ConfirmResp
>('mFA_Enroll_Confirm', api.auth.mFA_Enroll_Confirm);

// Second sign-in step after SignIn returned MFA == VerifyRequired. A recovery
// code also completes the sign-in, and turns MFA off for the account.
export const mfaSignInVerify = generateAuthThunk<
  MFA_SignIn_VerifyReq,
  AuthState
>('mFA_SignIn_Verify', api.auth.mFA_SignIn_Verify);
